import React, { useContext } from 'react';

import { useSelector } from 'react-redux';
import { Link } from 'react-resource-router';

import { AvatarPropTypes } from '@atlaskit/avatar';

import { CommitList as BaseCommitList } from 'src/components/commit-list';
import { CommitAuthor } from 'src/components/types';
import {
  UnmatchedCommitAuthorAvatar,
  UnmatchedCommitAuthorName,
} from 'src/components/user-profile';
import { UserAvatar } from 'src/containers/user-avatar';
import { UserName } from 'src/containers/user-name';
import { getIsCommitExpandEnabled } from 'src/selectors/feature-selectors';
import { MetricContext } from 'src/utils/metrics-provider';

type CommitListProps = JSX.LibraryManagedAttributes<
  typeof BaseCommitList,
  BaseCommitList['props']
> & {};

const renderAuthorAvatar = (
  author: CommitAuthor,
  avatarProps: Partial<AvatarPropTypes>
) =>
  author.user ? (
    <UserAvatar
      {...avatarProps}
      profileCardPosition="bottom-start"
      user={author.user}
    />
  ) : (
    // Do not pass `rawCommitAuthor` in, because we already provide the raw author's name
    // in the same UI context (via `<UnmatchedCommitAuthorName />`)
    <UnmatchedCommitAuthorAvatar {...avatarProps} />
  );

const renderAuthorName = (author: CommitAuthor) =>
  author.user ? (
    <UserName
      key={author.user.account_id}
      profileCardPosition="bottom-start"
      user={author.user}
    />
  ) : (
    <UnmatchedCommitAuthorName key={author.raw} rawCommitAuthor={author.raw} />
  );

// this is purely to conform to the typing required by the commit-list linkComponent
const CommitLink = (props: { href?: string; to?: string; children: any }) => {
  return <Link {...props} />;
};

export const CommitList = ({ ...props }: CommitListProps) => {
  const isCommitExpandEnabled = useSelector(getIsCommitExpandEnabled);
  const sendMetric = useContext(MetricContext);

  if (!props.isLoading && props.commits?.length) {
    sendMetric();
  }
  return (
    <BaseCommitList
      renderAuthorAvatar={renderAuthorAvatar}
      renderAuthorName={renderAuthorName}
      isCommitExpandEnabled={isCommitExpandEnabled}
      linkComponent={CommitLink}
      {...props}
    />
  );
};
